import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  alpha,
  FormControlLabel,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  OpenInNew,
  CleaningServices,
  Warning as WarningIcon,
  Api,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  integrationApiService,
  ShopifyDuplicateMappingGroup,
  ShopifyOrphanSuspectGroup,
  ShopifyOrphanSuspectRow,
  IntegrationConfig,
} from '../services/integrationApi';
import { getApiUrl } from '../utils/apiConfig';
import { PageContainer, PageHeader } from '../components/ui';
import { tokens } from '../theme/tokens';

function normalizeShopAdminHost(credentials: Record<string, any> | undefined): string {
  let s = String(credentials?.shopId || credentials?.shopDomain || '').trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').split('/')[0];
  if (!s) return '';
  if (!s.includes('.')) return `${s}.myshopify.com`;
  return s;
}

function shopifyAdminProductUrl(host: string, numericProductId: string): string {
  if (!host) return '#';
  return `https://${host}/admin/products/${numericProductId}`;
}

function platformProductIdsForDocIds(
  groups: ShopifyDuplicateMappingGroup[],
  docIds: string[],
): string[] {
  const out = new Set<string>();
  for (const g of groups) {
    for (const row of g.rows) {
      if (docIds.includes(row.docId) && row.platformProductId) out.add(row.platformProductId);
    }
  }
  return Array.from(out);
}

/** Trailing numeric segment Shopify adds for duplicate handles, e.g. `lens-name-1`. */
const HANDLE_NUMERIC_SUFFIX = /-(\d+)$/;

/**
 * One row per group to keep unchecked — likely the “real” listing: prefer no -N suffix, then most images, then lower Shopify id.
 */
function pickOrphanKeeper(rows: ShopifyOrphanSuspectRow[]): string | null {
  if (!rows.length) return null;
  const noSuffix = rows.filter((r) => !HANDLE_NUMERIC_SUFFIX.test((r.handle || '').trim()));
  const pool = noSuffix.length ? noSuffix : rows;
  const sorted = [...pool].sort((a, b) => {
    const icA = a.imageCount ?? 0;
    const icB = b.imageCount ?? 0;
    if (icB !== icA) return icB - icA;
    return String(a.shopifyProductId).localeCompare(String(b.shopifyProductId), undefined, { numeric: true });
  });
  return sorted[0]?.shopifyProductId ?? null;
}

/** Preselect suspected duplicates: handle ends with -1, -2, … and/or zero images; never the keeper row. */
function computeDefaultOrphanSelection(groups: ShopifyOrphanSuspectGroup[]): Set<string> {
  const next = new Set<string>();
  for (const g of groups) {
    const keeper = pickOrphanKeeper(g.suspectedOrphans);
    for (const row of g.suspectedOrphans) {
      if (keeper && row.shopifyProductId === keeper) continue;
      const handle = (row.handle || '').trim();
      const hasSuffix = HANDLE_NUMERIC_SUFFIX.test(handle);
      const zeroImg = (row.imageCount ?? 0) === 0;
      if (hasSuffix || zeroImg) next.add(row.shopifyProductId);
    }
  }
  return next;
}

const IntegrationShopifyDuplicatesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [apiConfirmOpen, setApiConfirmOpen] = useState(false);
  const [orphanConfirmOpen, setOrphanConfirmOpen] = useState(false);
  const [selectedRemove, setSelectedRemove] = useState<Set<string>>(new Set());
  const [selectedOrphanIds, setSelectedOrphanIds] = useState<Set<string>>(new Set());
  const [deleteShopifyAlso, setDeleteShopifyAlso] = useState(false);

  const { data: integration, isLoading: intLoading, error: intError } = useQuery({
    queryKey: ['integration', id],
    queryFn: () => integrationApiService.getIntegration(id!),
    enabled: !!id,
  });

  const {
    data: dupData,
    isLoading: dupLoading,
    error: dupError,
    refetch,
  } = useQuery({
    queryKey: ['shopifyDuplicateMappings', id],
    queryFn: () => integrationApiService.getShopifyDuplicateMappings(id!),
    enabled: !!id && integration?.platform === 'shopify',
  });

  const {
    data: orphanData,
    isLoading: orphanLoading,
    error: orphanError,
    refetch: refetchOrphans,
  } = useQuery({
    queryKey: ['shopifyOrphanSuspects', id],
    queryFn: () => integrationApiService.getShopifyOrphanSuspects(id!),
    enabled: !!id && integration?.platform === 'shopify',
    staleTime: 60_000,
  });

  const groups = dupData?.groups ?? [];
  const orphanGroups = orphanData?.groups ?? [];

  useEffect(() => {
    const all = new Set<string>();
    for (const g of groups) {
      g.recommendedRemoveDocIds.forEach((d) => all.add(d));
    }
    setSelectedRemove(all);
  }, [groups]);

  useEffect(() => {
    const og = orphanData?.groups;
    if (!og?.length) {
      setSelectedOrphanIds(new Set());
      return;
    }
    setSelectedOrphanIds(computeDefaultOrphanSelection(og));
  }, [orphanData]);

  const shopHost = useMemo(
    () => normalizeShopAdminHost((integration as IntegrationConfig)?.credentials),
    [integration],
  );

  const removeMutation = useMutation({
    mutationFn: (args: { docIds: string[]; deleteShopifyProductIds?: string[] }) =>
      integrationApiService.removeShopifyDuplicateMappings(id!, args.docIds, args.deleteShopifyProductIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyDuplicateMappings', id] });
      queryClient.invalidateQueries({ queryKey: ['shopifyOrphanSuspects', id] });
      setConfirmOpen(false);
      setDeleteShopifyAlso(false);
    },
  });

  const toggleDoc = (docId: string, checked: boolean) => {
    setSelectedRemove((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  const selectedList = Array.from(selectedRemove);
  const apiIdsForSelection = platformProductIdsForDocIds(groups, selectedList);

  const apiDeleteMutation = useMutation({
    mutationFn: () =>
      integrationApiService.deleteShopifyDuplicateProductsViaApi(id!, apiIdsForSelection),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyDuplicateMappings', id] });
      queryClient.invalidateQueries({ queryKey: ['shopifyOrphanSuspects', id] });
      setApiConfirmOpen(false);
    },
  });

  const orphanDeleteMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      integrationApiService.deleteShopifyDuplicateProductsViaApi(id!, productIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopifyOrphanSuspects', id] });
      queryClient.invalidateQueries({ queryKey: ['shopifyDuplicateMappings', id] });
      setOrphanConfirmOpen(false);
      setSelectedOrphanIds(new Set());
    },
  });

  const toggleOrphan = (productId: string, checked: boolean) => {
    setSelectedOrphanIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(productId);
      else next.delete(productId);
      return next;
    });
  };

  const selectedOrphanList = Array.from(selectedOrphanIds);

  if (intLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (intError || !integration) {
    return (
      <PageContainer>
        <Alert severity="error">Integration not found or failed to load.</Alert>
      </PageContainer>
    );
  }

  if (integration.platform !== 'shopify') {
    return (
      <PageContainer maxWidth={720}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/app/integrations/${id}`)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="info">Duplicate mapping cleanup is only available for Shopify integrations.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={1100}>
      <PageHeader
        title="Duplicate mapping cleanup"
        backTo={`/app/integrations/${id}`}
        backLabel="Back to integration"
        actions={
          <Button
            size="small"
            onClick={() => {
              refetch();
              refetchOrphans();
            }}
            disabled={dupLoading || orphanLoading}
          >
            Refresh
          </Button>
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Each row should have a single Motzu mapping per canonical product. Extra rows usually mean duplicate Shopify
        listings created from overlapping syncs. Check which Shopify product to keep in{' '}
        <strong>Shopify admin</strong>, then remove only the mapping rows that point at the duplicate listing.
      </Typography>

      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2, borderRadius: 2 }}>
        By default, removing a mapping only deletes the Motzu <strong>Firestore</strong> row. You can optionally delete
        the duplicate <strong>Shopify</strong> listing in the confirmation step (after mappings are removed), if you are
        sure that product should not remain in your store.
      </Alert>

      {dupError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(dupError as Error).message}
        </Alert>
      )}

      {removeMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(removeMutation.error as Error).message}
        </Alert>
      )}

      {apiDeleteMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(apiDeleteMutation.error as Error).message}
        </Alert>
      )}

      {orphanError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(orphanError as Error).message}
        </Alert>
      )}

      {orphanDeleteMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(orphanDeleteMutation.error as Error).message}
        </Alert>
      )}

      {removeMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Removed {removeMutation.data?.deleted ?? 0} Firestore mapping document(s).
          {(removeMutation.data?.shopifyProductsDeleted ?? 0) > 0
            ? ` Deleted ${removeMutation.data?.shopifyProductsDeleted} duplicate product(s) in Shopify.`
            : ''}
        </Alert>
      )}

      {apiDeleteMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Motzu API: removed {apiDeleteMutation.data?.deletedMappings ?? 0} mapping document(s) and deleted{' '}
          {apiDeleteMutation.data?.shopifyProductsDeleted ?? 0} product(s) in Shopify.
        </Alert>
      )}

      {orphanDeleteMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Deleted {orphanDeleteMutation.data?.shopifyProductsDeleted ?? 0} orphan product(s) in Shopify.
        </Alert>
      )}

      {dupLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No duplicate mapping groups found for this integration.</Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {groups.length} product{groups.length === 1 ? '' : 's'} with duplicate mappings
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CleaningServices />}
              disabled={selectedList.length === 0 || removeMutation.isPending || apiDeleteMutation.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              Remove selected mappings ({selectedList.length})
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Api />}
              disabled={selectedList.length === 0 || apiDeleteMutation.isPending || removeMutation.isPending}
              onClick={() => setApiConfirmOpen(true)}
            >
              Delete selected via Motzu API
            </Button>
          </Box>

          {groups.map((group: ShopifyDuplicateMappingGroup) => (
            <Paper key={group.productId} variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(tokens.accent.main, 0.04), borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {group.productName || group.productId}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: tokens.font.mono }}>
                  {group.productId}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Firestore doc</TableCell>
                      <TableCell>Shopify product</TableCell>
                      <TableCell>Last synced</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.rows.map((row) => {
                      const isKeep = row.docId === group.recommendedKeepDocId;
                      const canRemove = !isKeep;
                      const checked = selectedRemove.has(row.docId);
                      return (
                        <TableRow key={row.docId} selected={isKeep}>
                          <TableCell padding="checkbox">
                            {canRemove ? (
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={(_, c) => toggleDoc(row.docId, c)}
                              />
                            ) : (
                              <Checkbox size="small" checked={false} disabled />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: tokens.font.mono, fontSize: '0.75rem' }}>{row.docId}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2">{row.platformProductId}</Typography>
                              <Tooltip title="Open in Shopify admin">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={shopifyAdminProductUrl(shopHost, row.platformProductId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <OpenInNew sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {row.lastSyncedAt
                              ? new Date(row.lastSyncedAt).toLocaleString()
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {isKeep ? (
                              <Chip size="small" label="Recommended keep" color="success" variant="outlined" />
                            ) : row.isDeterministicDoc ? (
                              <Chip size="small" label="Deterministic id" variant="outlined" />
                            ) : (
                              <Chip size="small" label="Recommended remove" color="secondary" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !removeMutation.isPending && setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>Remove duplicate mappings?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will delete {selectedList.length} mapping document(s) from Motzu for duplicate Shopify listings you do
            not want linked.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteShopifyAlso}
                onChange={(_, c) => setDeleteShopifyAlso(c)}
                disabled={removeMutation.isPending}
              />
            }
            label="Also delete these Shopify products (permanent)"
            sx={{ alignItems: 'flex-start', ml: 0, mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, pl: 0.5 }}>
            Runs after mappings are removed. Only listings tied to the selected mapping rows are eligible. Prefer
            archiving in Shopify if the duplicate had orders or SEO traffic.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Only organization admins can perform this action.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={removeMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={removeMutation.isPending}
            onClick={() =>
              removeMutation.mutate({
                docIds: selectedList,
                deleteShopifyProductIds: deleteShopifyAlso
                  ? platformProductIdsForDocIds(groups, selectedList)
                  : undefined,
              })
            }
          >
            {removeMutation.isPending ? 'Removing…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={apiConfirmOpen}
        onClose={() => !apiDeleteMutation.isPending && setApiConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete duplicate listings via Motzu API?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Calls{' '}
            <Typography component="span" variant="body2" sx={{ fontFamily: tokens.font.mono, fontSize: '0.8rem' }}>
              POST …/shopify-duplicate-mappings/delete-shopify-products
            </Typography>{' '}
            with the selected numeric Shopify product IDs. Motzu removes the extra mapping rows, then deletes those
            products in Shopify.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: tokens.font.mono, mb: 1 }}>
            {apiIdsForSelection.join(', ') || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cannot target the &quot;recommended keep&quot; listing. Organization admins only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiConfirmOpen(false)} disabled={apiDeleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={apiDeleteMutation.isPending || apiIdsForSelection.length === 0}
            onClick={() => apiDeleteMutation.mutate()}
          >
            {apiDeleteMutation.isPending ? 'Working…' : 'Confirm API delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Orphan listings (same title, unmapped)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Motzu-tagged products that share a title with a mapped Shopify listing but have no Firestore mapping — often a
        duplicate left after a failed update. Checkboxes default to{' '}
        <strong>handles ending in -1, -2, …</strong> or <strong>0 images</strong>; the row that likely matches your real
        listing (no numeric handle suffix when another row has one, otherwise most images) stays unchecked. Adjust before
        deleting. Confirm in Shopify (orders, SEO) first.
      </Typography>

      {orphanLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Tag <code>{orphanData?.tag ?? 'motzu_synced'}</code> · {orphanData?.motzuTaggedProductCount ?? 0} Motzu-tagged
            products in Shopify · {orphanData?.mappedShopifyIdCount ?? 0} mapped Shopify IDs in Motzu
          </Typography>

          {orphanGroups.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">No orphan suspects found.</Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {orphanGroups.length} title group{orphanGroups.length === 1 ? '' : 's'} with suspected orphans
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => orphanData?.groups && setSelectedOrphanIds(computeDefaultOrphanSelection(orphanData.groups))}
                  disabled={!orphanData?.groups?.length}
                >
                  Reapply default selection
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<CleaningServices />}
                  disabled={selectedOrphanList.length === 0 || orphanDeleteMutation.isPending}
                  onClick={() => setOrphanConfirmOpen(true)}
                >
                  Delete selected in Shopify ({selectedOrphanList.length})
                </Button>
              </Box>

              {orphanGroups.map((g: ShopifyOrphanSuspectGroup, gi: number) => (
                <Paper
                  key={`${g.normalizedTitle}-${g.mappedShopifyIds.join(',')}-${gi}`}
                  variant="outlined"
                  sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: alpha(tokens.signal.info.solid, 0.06),
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {g.normalizedTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mapped in Motzu (keep): {g.mappedShopifyIds.join(', ')}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Suspected orphan</TableCell>
                          <TableCell>Images</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Handle</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {g.suspectedOrphans.map((row) => {
                          const checked = selectedOrphanIds.has(row.shopifyProductId);
                          return (
                            <TableRow key={row.shopifyProductId}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={(_, c) => toggleOrphan(row.shopifyProductId, c)}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="body2">{row.shopifyProductId}</Typography>
                                  <Tooltip title="Open in Shopify admin">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={row.adminUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <OpenInNew sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {row.title}
                                </Typography>
                              </TableCell>
                              <TableCell>{row.imageCount}</TableCell>
                              <TableCell>{row.status}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{row.handle}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))}
            </>
          )}
        </>
      )}

      <Dialog
        open={orphanConfirmOpen}
        onClose={() => !orphanDeleteMutation.isPending && setOrphanConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete orphan listings in Shopify?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Permanently deletes the selected products in Shopify. They have no Motzu mapping — use only after you
            confirm they are redundant duplicates.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: tokens.font.mono, mb: 1 }}>
            {selectedOrphanList.join(', ') || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Organization admins only. Prefer archiving in Shopify if a listing had orders or traffic.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrphanConfirmOpen(false)} disabled={orphanDeleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={orphanDeleteMutation.isPending || selectedOrphanList.length === 0}
            onClick={() => orphanDeleteMutation.mutate(selectedOrphanList)}
          >
            {orphanDeleteMutation.isPending ? 'Deleting…' : 'Confirm delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ mt: 3, p: 2.5, borderRadius: 2, bgcolor: alpha(tokens.ink.primary, 0.02) }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Api fontSize="small" color="action" />
          API: duplicate &amp; orphan cleanup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Same actions as the buttons above, for scripts or automation. Use your session token or API key in{' '}
          <code>Authorization: Bearer</code>.
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75 }}>
          <code style={{ fontSize: '0.72rem' }}>
            GET {getApiUrl()}/integrations/{id}/shopify-orphan-suspects
          </code>
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.100',
            fontSize: '0.7rem',
            overflow: 'auto',
            fontFamily: tokens.font.mono,
            mb: 1.5,
          }}
        >
          {`curl -s "${getApiUrl()}/integrations/${id}/shopify-orphan-suspects" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75 }}>
          <code style={{ fontSize: '0.72rem' }}>
            POST {getApiUrl()}/integrations/{id}/shopify-duplicate-mappings/delete-shopify-products
          </code>
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.100',
            fontSize: '0.7rem',
            overflow: 'auto',
            fontFamily: tokens.font.mono,
          }}
        >
          {`curl -X POST "${getApiUrl()}/integrations/${id}/shopify-duplicate-mappings/delete-shopify-products" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"shopifyProductIds":["1234567890"]}'`}
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default IntegrationShopifyDuplicatesPage;
